import { supabase } from '@/supabase'

class CommunityAlertService {
  constructor() {
    this.supabase = supabase
  }

  // ============================================================
  // Disease Reports — Farmer-facing
  // ============================================================

  async submitDiseaseReport(reportData) {
    const { data: { user }, error: userError } = await this.supabase.auth.getUser()
    if (userError || !user) throw new Error('User not authenticated')

    const { data, error } = await this.supabase
      .from('disease_reports')
      .insert([{
        user_id: user.id,
        disease_name: reportData.disease_name,
        symptoms: reportData.symptoms,
        affected_count: reportData.affected_count || 0,
        severity: reportData.severity || 'moderate',
        description: reportData.description || null,
        photos: reportData.photos || [],
        location: reportData.location || null,
        status: 'pending'
      }])
      .select()

    if (error) throw error
    return data[0]
  }

  async getMyReports() {
    const { data: { user }, error: userError } = await this.supabase.auth.getUser()
    if (userError || !user) throw new Error('User not authenticated')

    const { data, error } = await this.supabase
      .from('disease_reports')
      .select('*')
      .eq('user_id', user.id)
      .eq('is_deleted', false)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  }

  // ============================================================
  // Disease Reports — Admin-facing
  // ============================================================

  async getPendingReports() {
    const { data, error } = await this.supabase
      .from('disease_reports')
      .select('*')
      .eq('status', 'pending')
      .eq('is_deleted', false)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  }

  async getAllReports(filters = {}) {
    let query = this.supabase
      .from('disease_reports')
      .select('*')
      .eq('is_deleted', false)

    if (filters.status) {
      query = query.eq('status', filters.status)
    }
    if (filters.severity) {
      query = query.eq('severity', filters.severity)
    }

    query = query.order('created_at', { ascending: false })

    const { data, error } = await query
    if (error) throw error
    return data || []
  }

  async approveReport(reportId, alertData) {
    const { data: { user }, error: userError } = await this.supabase.auth.getUser()
    if (userError || !user) throw new Error('User not authenticated')

    // 1. Update the disease report status
    const { error: reportError } = await this.supabase
      .from('disease_reports')
      .update({
        status: 'approved',
        admin_notes: alertData.admin_notes || null,
        reviewed_by: user.id,
        reviewed_at: new Date().toISOString()
      })
      .eq('id', reportId)

    if (reportError) throw reportError

    // 2. Create the community alert
    const { data: alert, error: alertError } = await this.supabase
      .from('community_alerts')
      .insert([{
        disease_report_id: reportId,
        title: alertData.title,
        disease_name: alertData.disease_name,
        severity: alertData.severity,
        description: alertData.description || null,
        recommended_steps: alertData.recommended_steps,
        status: 'active',
        created_by: user.id
      }])
      .select()

    if (alertError) throw alertError
    return alert[0]
  }

  async rejectReport(reportId, adminNotes) {
    const { data: { user }, error: userError } = await this.supabase.auth.getUser()
    if (userError || !user) throw new Error('User not authenticated')

    const { data, error } = await this.supabase
      .from('disease_reports')
      .update({
        status: 'rejected',
        admin_notes: adminNotes || null,
        reviewed_by: user.id,
        reviewed_at: new Date().toISOString()
      })
      .eq('id', reportId)
      .select()

    if (error) throw error
    return data[0]
  }

  // ============================================================
  // Community Alerts — All users
  // ============================================================

  async getActiveAlerts() {
    const { data, error } = await this.supabase
      .from('community_alerts')
      .select('*')
      .eq('is_deleted', false)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  }

  async getAlertById(alertId) {
    const { data, error } = await this.supabase
      .from('community_alerts')
      .select('*')
      .eq('id', alertId)
      .single()

    if (error) throw error
    return data
  }

  async resolveAlert(alertId) {
    const { data, error } = await this.supabase
      .from('community_alerts')
      .update({ status: 'resolved' })
      .eq('id', alertId)
      .select()

    if (error) throw error
    return data[0]
  }

  // ============================================================
  // Notification tracking
  // ============================================================

  async markAlertAsRead(alertId) {
    const { data: { user }, error: userError } = await this.supabase.auth.getUser()
    if (userError || !user) throw new Error('User not authenticated')

    const { error } = await this.supabase
      .from('user_alert_reads')
      .upsert({
        user_id: user.id,
        alert_id: alertId,
        read_at: new Date().toISOString()
      }, { onConflict: 'user_id,alert_id' })

    if (error) throw error
  }

  async getUnreadCount() {
    const { data: { user }, error: userError } = await this.supabase.auth.getUser()
    if (userError || !user) return 0

    const { data, error } = await this.supabase
      .rpc('get_unread_alert_count', { p_user_id: user.id })

    if (error) return 0
    return data || 0
  }

  async getDashboardAlerts(limit = 3) {
    const { data, error } = await this.supabase
      .from('community_alerts')
      .select('*')
      .eq('is_deleted', false)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) throw error
    return data || []
  }

  // ============================================================
  // Auto-report from health records
  // ============================================================

  get reportableDiseases() {
    return {
      'Respiratory Infection': 'Respiratory Infection',
      'Snuffles': 'Snuffles',
      'GI Stasis': 'GI Stasis',
      'Ear Mites': 'Ear Mites',
      'Dental Issues': 'Dental Disease',
      'Skin Irritation': 'Skin Disease'
    }
  }

  async autoCreateDiseaseReport(healthRecord) {
    if (!healthRecord || !healthRecord.condition) return

    const diseaseName = this.reportableDiseases[healthRecord.condition]
    if (!diseaseName) return

    const { data: { user }, error: userError } = await this.supabase.auth.getUser()
    if (userError || !user) return

    // Check if user already has a pending report for this disease recently (last 7 days)
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
    const { data: existing } = await this.supabase
      .from('disease_reports')
      .select('id')
      .eq('user_id', user.id)
      .eq('disease_name', diseaseName)
      .eq('status', 'pending')
      .gte('created_at', sevenDaysAgo)
      .limit(1)

    if (existing && existing.length > 0) return

    const { error } = await this.supabase
      .from('disease_reports')
      .insert([{
        user_id: user.id,
        disease_name: diseaseName,
        symptoms: healthRecord.symptoms || healthRecord.condition,
        affected_count: 1,
        severity: healthRecord.severity || 'moderate',
        description: healthRecord.notes || null,
        status: 'pending'
      }])

    if (error) console.error('Auto disease report creation failed:', error)
  }
}

export const communityAlertService = new CommunityAlertService()
